import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function AboutPreview() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const stats = [
    { numberKey: "aboutPreview.stat1.number", labelKey: "aboutPreview.stat1.label" },
    { numberKey: "aboutPreview.stat2.number", labelKey: "aboutPreview.stat2.label" },
    { numberKey: "aboutPreview.stat3.number", labelKey: "aboutPreview.stat3.label" },
  ];

  return (
    <section className="bg-white py-20 md:py-28" ref={ref}>
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div
            className={`transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <p className="text-xs uppercase tracking-widest text-gold font-medium mb-4">
              {t("aboutPreview.label")}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-6 leading-snug">
              {t("aboutPreview.title")}
            </h2>
            <hr className="border-cool-gray-200 mb-6 w-16" />
            <p className="text-navy/70 text-base leading-relaxed mb-5">
              {t("aboutPreview.p1")}
            </p>
            <p className="text-navy/70 text-base leading-relaxed mb-8">
              {t("aboutPreview.p2")}
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-navy font-semibold text-sm hover:text-gold transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              {t("aboutPreview.link")}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-base" />
              </span>
            </Link>
          </div>

          <div
            className={`transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <div className="relative w-full h-80 md:h-96 overflow-hidden rounded-lg">
              <img
                src="https://public.readdy.ai/ai/img_res/6f3045133ce461bffb9390499b169bf6.jpg"
                alt="EU Next Grants team collaborating"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-navy/10 to-transparent rounded-lg" />
            </div>
            <div className="mt-6 grid grid-cols-3 gap-4">
              {stats.map((stat) => (
                <div key={stat.labelKey} className="border-l-2 border-gold pl-4">
                  <p className="text-xl font-bold text-navy">{t(stat.numberKey)}</p>
                  <p className="text-xs text-navy/50 mt-0.5">{t(stat.labelKey)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
